import { useEffect, useRef, useState } from 'react';
import { fuentesPorCategoria } from '@besbello/design-core';
import { cargarFuentes } from '../editor/cargar-fuentes.js';

/**
 * SELECTOR DE TIPOGRAFÍA QUE ENSEÑA LAS LETRAS.
 *
 * Antes era un `<select>` nativo, y un `<option>` no puede pintarse con su
 * propia fuente (en móvil el sistema dibuja la lista a su manera). Elegir entre
 * «Bangers» y «Bebas Neue» por el nombre es elegir a ciegas: nadie que no sea
 * diseñador sabe qué letra hay detrás de cada nombre.
 *
 * Es un desplegable EN LÍNEA, no flotante: el panel de propiedades vive en un
 * contenedor con scroll y un popover absoluto acabaría recortado por el
 * `overflow`. Al expandirse empuja el contenido, que en un panel lateral es
 * exactamente lo que se espera.
 *
 * Las fuentes se piden AL ABRIR, no antes: ~20 familias a peso 400 solo cuando
 * alguien va a elegir. `cargarFuentes` ya trae caché de lo pedido y nunca
 * rechaza (comprobado en su cabecera), así que abrir dos veces no descarga dos
 * veces y una fuente que no llegue se pinta con la de sustitución.
 */
/** El catálogo es constante: agrupado una vez a nivel de módulo. */
const GRUPOS = fuentesPorCategoria();

export function SelectorFuente(props: {
  value: string;
  /** Texto de la capa, para que la muestra sea EL texto del cliente. */
  muestra?: string;
  onChange: (family: string) => void;
  etiquetaGrupo: (categoria: string) => string;
}) {
  const [abierto, setAbierto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);

  // La muestra: el texto real del diseño si lo hay, recortado para que quepa.
  const muestra = (props.muestra ?? '').trim().slice(0, 18) || null;

  useEffect(() => {
    if (!abierto) return;
    // Todas a peso 400: para VER la letra basta un peso, los demás se cargan
    // cuando la capa los pida de verdad.
    void cargarFuentes(GRUPOS.flatMap((g) => g.fuentes.map((f) => ({ family: f.family, weight: 400, italic: false }))));

    const alPulsar = (e: PointerEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    window.addEventListener('pointerdown', alPulsar);
    window.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('pointerdown', alPulsar);
      window.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  return (
    <div ref={raiz}>
      <button
        type="button"
        data-test="fuente"
        aria-expanded={abierto}
        onClick={() => setAbierto((v) => !v)}
        className="toque flex w-full items-center justify-between rounded-lg border border-borde px-3 py-2 text-left"
      >
        {/* El propio botón ya enseña la letra elegida. */}
        <span className="truncate text-base" style={{ fontFamily: `'${props.value}'` }}>
          {muestra ?? props.value}
        </span>
        <span className="ml-2 shrink-0 text-xs text-tinta-suave">{props.value}</span>
      </button>

      {abierto && (
        <ul role="listbox" aria-label={props.value} className="mt-1 max-h-72 overflow-y-auto rounded-lg border border-borde bg-white">
          {GRUPOS.map(({ categoria, fuentes }) => (
            <li key={categoria}>
              <p className="sticky top-0 bg-papel-suave px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-tinta-suave">
                {props.etiquetaGrupo(categoria)}
              </p>
              <ul>
                {fuentes.map((f) => (
                  <li key={f.family}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={f.family === props.value}
                      data-test="fuente-opcion"
                      onClick={() => {
                        props.onChange(f.family);
                        setAbierto(false);
                      }}
                      className={`toque flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left hover:bg-papel-suave ${
                        f.family === props.value ? 'bg-papel-suave' : ''
                      }`}
                    >
                      {/* Cada opción pintada CON SU LETRA: es todo el sentido
                          de este componente. El texto es el del cliente si lo
                          hay; si no, el nombre de la familia. */}
                      <span className="truncate text-lg leading-6" style={{ fontFamily: `'${f.family}'` }}>
                        {muestra ?? f.family}
                      </span>
                      <span className="shrink-0 text-[11px] text-tinta-suave">{f.para}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
